import React from 'react';

import { translate } from '~l10n/client';
import { Breadcrumbs } from '~shared/components/Breadcrumbs';
import { useWorkspaceList } from '~teacher/hook/workspaces';
import { useWorkspaceFolders } from '~teacher/hook/workspaces/useWorkspaceFolders';
import { WorkspaceItem } from '~teacher/hook/workspaces/useWorkspaceItems';

import { WorkspaceFolder } from '../../../../api/workspaces/folders/types';
import { Workspace } from '../../../../api/workspaces/types';

interface Props {
  currentItem: WorkspaceItem;
  workspaceId: string;
  hideCurrent?: boolean;
}

const MAX_ITERATIONS = 30; // Safety limit

export const WorkspaceQuizzesBreadcrumbs = ({
  currentItem,
  workspaceId,
  hideCurrent = false,
}: Props) => {
  const allFolders = useWorkspaceFolders({ workspaceId });
  const currentWorkspace = useWorkspaceList({
    select: (workspaces) => workspaces.find((w) => w.id === workspaceId),
  });

  const crumbs = getWorkspacesBreadCrumbs({
    currentItem: currentItem,
    allFolders: allFolders.data,
    rootWorkspace: currentWorkspace.data,
  });

  return <Breadcrumbs hideCurrent={hideCurrent} crumbs={crumbs} />;
};

function getParentId(workspaceItem: WorkspaceItem) {
  switch (workspaceItem.type) {
    case 'folder':
      return workspaceItem.parentId;
    case 'quiz':
      return workspaceItem.parentFolderId;
    default:
      return null;
  }
}

interface GetWorkspacesBreadCrumbsParams {
  currentItem: WorkspaceItem;
  allFolders: WorkspaceFolder[];
  rootWorkspace: Workspace;
  crumbs?: Array<{ label: string; href: string }>;
}

export function getWorkspacesBreadCrumbs({
  currentItem,
  allFolders,
  rootWorkspace,
  crumbs = [],
}: GetWorkspacesBreadCrumbsParams) {
  if (!rootWorkspace) {
    return [
      { label: translate('Personal') as string, href: '#quizzes' },
      ...crumbs,
    ];
  }

  if (currentItem == null || allFolders == null || !rootWorkspace) {
    return [
      {
        label: rootWorkspace.name,
        href: `#workspaces/${rootWorkspace.id}/library`,
      },
      ...crumbs,
    ];
  }

  // Build breadcrumbs iteratively
  const breadcrumbs: Array<{ label: string; href: string }> = [...crumbs];
  let current = currentItem;
  let iterationCount = 0;

  while (current != null && iterationCount < MAX_ITERATIONS) {
    // Add current item to the beginning of breadcrumbs
    breadcrumbs.unshift({
      label: current.name,
      href:
        current.type === 'folder'
          ? `#workspaces/${rootWorkspace.id}/library/${current.id}`
          : `#workspaces/${rootWorkspace.id}/quizzes/${current.id}`,
    });

    // Find parent and continue up the hierarchy
    const parentId = getParentId(current);
    current = allFolders.find((folder) => folder.id === parentId) || null;
    iterationCount++;
  }

  // Log warning if we hit the safety limit
  if (iterationCount >= MAX_ITERATIONS) {
    console.warn(
      `Breadcrumb generation hit safety limit of ${MAX_ITERATIONS} iterations. Possible circular reference detected.`
    );
  }

  // Add root workspace at the beginning
  breadcrumbs.unshift({
    label: rootWorkspace.name,
    href: `#workspaces/${rootWorkspace.id}/library`,
  });

  return breadcrumbs;
}
